import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Download } from "lucide-react";

const ROLES = { admin: "Yönetici", technician: "Usta", dealer: "Bayi", customer: "Müşteri", unknown: "Kayıtsız hesap" };
const WHY = { bad_password: "Hatalı şifre", locked: "Hesap kilitli (çok deneme)", need_phone: "Telefon istendi", phone_mismatch: "Telefon uyuşmadı", blocked_device: "Engelli cihaz", pending_device: "Cihaz onay bekliyor", inactive: "Hesap pasif", bad_otp: "Doğrulama kodu hatalı" };
const dt = (s) => (s ? new Date(s).toLocaleString("tr-TR") : "-");

export default function FailedLogins() {
  const [days, setDays] = useState("7");
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [sum, setSum] = useState(null);

  const load = useCallback(async () => {
    const params = { success: false, days: +days, limit: 1000 };
    if (role !== "all") params.role = role;
    if (q.trim()) params.q = q.trim();
    const [l, s] = await Promise.all([api.get("/security/logins", { params }), api.get("/security/failed-summary", { params: { days: +days } })]);
    setRows(l.data); setSum(s.data);
  }, [days, role, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const csv = () => {
    const head = ["Zaman", "Hesap", "Ad", "Rol", "Hesap var mı", "IP", "Cihaz", "Tarayıcı", "Neden"];
    const lines = rows.map((r) => [dt(r.at), r.email, r.name, ROLES[r.role] || (r.user_exists === false ? ROLES.unknown : r.role), r.user_exists === false ? "Hayır" : "Evet", r.ip, r.device_label, (r.ua || "").replace(/[\r\n;]/g, " "), WHY[r.reason] || r.reason].map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(";"));
    const blob = new Blob(["﻿" + [head.join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "hatali-girisler.csv"; a.click();
  };

  return (
    <div className="space-y-4" data-testid="failed-logins">
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={days} onValueChange={setDays}><SelectTrigger className="w-36"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">Son 24 saat</SelectItem><SelectItem value="7">Son 7 gün</SelectItem><SelectItem value="30">Son 30 gün</SelectItem><SelectItem value="90">Son 90 gün</SelectItem></SelectContent></Select>
        <Select value={role} onValueChange={setRole}><SelectTrigger className="w-44"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tüm roller</SelectItem>{Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
        <Input className="w-64" placeholder="E-posta, ad veya IP ara…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fl-search" />
        <Button variant="outline" onClick={csv} className="ml-auto" data-testid="fl-csv"><Download className="h-4 w-4 mr-2" />CSV indir</Button>
      </div>

      {sum && (
        <div className="grid lg:grid-cols-3 gap-3">
          <Card className="p-4 shadow-none border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Toplam hatalı giriş</div>
            <div className="text-3xl font-bold mt-1" data-testid="fl-total">{sum.total}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">{Object.entries(sum.by_role).map(([k, v]) => <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{ROLES[k] || k}: <b>{v}</b></span>)}</div>
          </Card>
          <Card className="p-4 shadow-none border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">En çok deneyen IP'ler</div>
            {sum.top_ips.length === 0 ? <div className="text-sm text-slate-500">Kayıt yok</div> : sum.top_ips.slice(0, 6).map((x) => (
              <div key={x.ip} className="flex justify-between text-sm py-0.5"><span className="font-mono text-xs">{x.ip}</span><span><b>{x.count}</b> deneme · {x.accounts} hesap</span></div>))}
          </Card>
          <Card className="p-4 shadow-none border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">En çok hedeflenen hesaplar</div>
            {sum.top_accounts.length === 0 ? <div className="text-sm text-slate-500">Kayıt yok</div> : sum.top_accounts.slice(0, 6).map((x) => (
              <div key={x.email} className="flex justify-between text-sm py-0.5 gap-2"><span className="truncate">{x.email}{!x.exists && <em className="text-amber-600 not-italic text-xs"> (kayıtsız)</em>}</span><span className="shrink-0"><b>{x.count}</b> · {x.ips} IP</span></div>))}
          </Card>
        </div>
      )}

      <Card className="shadow-none border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left"><tr><th className="px-4 py-3">Zaman</th><th className="px-4 py-3">Denenen hesap</th><th className="px-4 py-3">Rol</th><th className="px-4 py-3">IP</th><th className="px-4 py-3">Cihaz / Tarayıcı</th><th className="px-4 py-3">Neden</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid="fl-row">
                <td className="px-4 py-2.5 whitespace-nowrap">{dt(r.at)}</td>
                <td className="px-4 py-2.5">{r.name || r.email}<div className="text-xs text-slate-400">{r.email}</div></td>
                <td className="px-4 py-2.5">{r.user_exists === false ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Kayıtsız hesap</span> : (ROLES[r.role] || r.role || "-")}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{r.ip}</td>
                <td className="px-4 py-2.5 text-xs">{r.device_label}<div className="text-slate-400 max-w-[260px] truncate" title={r.ua}>{r.ua}</div></td>
                <td className="px-4 py-2.5"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{WHY[r.reason] || r.reason}</span></td>
              </tr>))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Bu aralıkta hatalı giriş yok.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
